import React, { useState, useRef } from 'react';
import Formsy from 'formsy-react';
import {
  RemoteChipSelectFormsy,
  SelectFormsy,
  CheckboxFormsy,
  TextFieldFormsy,
  MultipleSelectFormsy,
} from 'components/formsy';

import { OrganisationDto, UserDto } from 'models/dtoTypes';
import { GetOrganisations } from 'services/openapiClient';

import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { generateNumberValidation, generateTextValidation } from 'utils/generateValidation';
import { transformOptionData } from 'utils/form';
import { GetUsers } from 'services/user';
import CurrentUser from 'auth/currentUser';
import { useTranslation } from 'react-i18next';

const PREFIX = 'CreateResourceModal';

const classes = {
  root: `${PREFIX}-root`,
  dialogContent: `${PREFIX}-dialogContent`,
  button: `${PREFIX}-button`,
  submitButton: `${PREFIX}-submitButton`,
  flashSpacer: `${PREFIX}-flashSpacer`,
  paper: `${PREFIX}-paper`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.dialogContent}`]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 500,
  },

  [`& .${classes.submitButton}`]: {
    minWidth: 100,
    height: 50,
  },

  [`& .${classes.flashSpacer}`]: {
    minHeight: 10,
  },

  [`& .${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  '& .form-container > *': {
    marginBottom: 15,
  },
}));

export interface IField {
  required?: boolean;
  label: string;
  widget: string;
  name: string;
  description?: string;
  options?: { alias: string; label: string }[];
  optionAliases?: string[];
  placeholder?: string;
  min?: number;
  max?: number;
}

export interface ISchema {
  fields: IField[];
}

interface Props<TAttributes> {
  label: string;
  open: boolean;
  schema: ISchema;
  submitText?: string;
  onClose: () => void;
  onSave?: (model: TAttributes) => Promise<void>;
}

function CreateResourceModal<TAttributes>(props: Props<TAttributes>): JSX.Element {
  const formRef = useRef<Formsy>(null);
  const { label, open, onClose, onSave, schema } = props;

  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('default');

  function getCurrentValues() {
    const formsyEl = formRef.current;
    return (formsyEl ? formsyEl.getCurrentValues() : {}) as TAttributes;
  }

  const handleClose = () => {
    onClose();
  };

  const disableButton = () => {
    console.log('disable button');
    setDisable(true);
  };

  const enableButton = () => {
    console.log('enable button');
    setDisable(false);
  };

  const handleSearchOrganisation = async (search: { query: string }) => {
    const resultWithCount = await GetOrganisations({
      pageIndex: 1,
      fullTextSearch: search.query || '',
      sortColumn: 'name',
    });
    return {
      data: resultWithCount.items || [],
    };
  };

  const handleSearchSmeUser = async (search: { query: string }) => {
    const resultWithCount = await GetUsers({
      pageIndex: 1,
      fullTextSearch: search.query || '',
      filters: [
        {
          column: 'organisationId',
          value: CurrentUser.getInstance().getOrganisationId(),
        },
        {
          column: 'role',
          value: 'sme',
        },
      ],
      sortColumn: 'name',
    });
    return {
      data: resultWithCount.items || [],
    };
  };

  const renderField = (field: IField) => {
    const { required, placeholder, label, widget, name, description, options, optionAliases, min, max } = field;
    switch (widget) {
      case 'text': {
        const rules = {
          characterMax: 200,
          characterMin: 1,
        };
        const validationRules = generateTextValidation(rules);
        return (
          <TextFieldFormsy
            disabled={loading}
            key={name}
            required={required}
            type="text"
            name={name}
            label={label}
            description={description}
            validations={validationRules.validations}
            validationErrors={validationRules.validationErrors}
          />
        );
      }
      case 'number': {
        const rules = { min, max };
        const validationRules = generateNumberValidation(rules);
        return (
          <TextFieldFormsy
            disabled={loading}
            key={name}
            required={required}
            type="number"
            name={name}
            label={label}
            description={description}
            validations={validationRules.validations}
            validationErrors={validationRules.validationErrors}
          />
        );
      }
      case 'textArea': {
        const rules = {
          characterMin: 1,
        };
        const validationRules = generateTextValidation(rules);
        return (
          <TextFieldFormsy
            multiline
            disabled={loading}
            key={name}
            required={required}
            type="text"
            name={name}
            label={label}
            description={description}
            validations={validationRules.validations}
            validationErrors={validationRules.validationErrors}
          />
        );
      }
      case 'select': {
        return (
          <SelectFormsy
            key={name}
            required={required}
            disabled={loading}
            name={name}
            label={label}
            placeholder={placeholder}
            description={description}
            variant="outlined"
            options={options || []}
          />
        );
      }
      case 'multipleSelect': {
        return (
          <MultipleSelectFormsy
            key={name}
            required={required}
            disabled={loading}
            name={name}
            label={label}
            placeholder={placeholder}
            description={description}
            variant="outlined"
            textFieldProps={{
              label: label,
              disabled: loading,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            options={optionAliases || []}
          />
        );
      }
      case 'checkbox': {
        return (
          <CheckboxFormsy
            key={name}
            required={required}
            disabled={loading}
            name={name}
            label={label}
            description={description}
            variant="outlined"
          />
        );
      }
      case 'organisationSelector': {
        return (
          <RemoteChipSelectFormsy
            key={name}
            disabled={loading}
            name={name}
            required={required}
            label={label}
            placeholder={t('begin-typing-an-organisation')}
            description={description}
            onSearchOptions={handleSearchOrganisation}
            optionRenderer={({ id, name }: OrganisationDto) => ({
              alias: id,
              label: name,
            })}
            variant="outlined"
          />
        );
      }
      case 'smeUserSelector': {
        return (
          <RemoteChipSelectFormsy
            key={name}
            required={true}
            disabled={loading}
            name={name}
            label={label}
            placeholder={t('begin-typing-a-business-user-email')}
            onSearchOptions={handleSearchSmeUser}
            optionRenderer={({ id, email }: UserDto) => ({
              alias: id,
              label: email,
            })}
            variant="outlined"
          />
        );
      }
      default: {
        console.error('unsupported widget type');
        return <></>;
      }
    }
  };

  async function handleCreate(e: React.FormEvent) {
    if (!onSave) {
      return;
    }
    e.stopPropagation();
    const model = getCurrentValues();
    const transformed = transformOptionData(model as Record<string, unknown>);
    console.log('nested model', transformed);
    setLoading(true);
    await onSave(transformed as TAttributes);
    setLoading(false);
  }

  // nested form is bad, change to element 'div'
  const renderCardContent = () => (
    <Formsy
      formElement="div"
      onValid={enableButton}
      onInvalid={disableButton}
      ref={formRef}
      className="form-container flex flex-col justify-center"
    >
      {schema.fields.map(renderField)}
    </Formsy>
  );

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      className={classes.root}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Box boxShadow={2}>
        <DialogTitle>{label}</DialogTitle>
      </Box>
      <DialogContent className={classes.dialogContent}>{renderCardContent()}</DialogContent>
      {loading && <LinearProgress />}
      <Box boxShadow={2}>
        <DialogActions>
          <Button disabled={loading} variant="outlined" onClick={handleClose} color="primary">
            {t('cancel')}
          </Button>
          <Button
            disabled={loading || disable}
            variant="contained"
            color="primary"
            autoFocus
            onClick={handleCreate}
          >
            {props?.submitText || t('submit')}
          </Button>
        </DialogActions>
      </Box>
    </StyledDialog>
  );
}

export default CreateResourceModal;
